/**
 * Presentation helpers shared by the app views (task 21.2).
 *
 * Monetary amounts arrive from the services as exact decimal strings (never
 * floats) to preserve 2-decimal precision. These formatters render them for
 * display without ever converting through a binary float, so no precision is
 * lost. Category and date helpers centralise the "Uncategorized" fallback (R5.3)
 * and locale date rendering.
 *
 * Also home to the small, pure presentation rules the feed and reverse views
 * share: natural ("Today"/"Yesterday") dates, a readable description derived
 * from category + sign (the Transaction type has no description field), and
 * the client-side mirror of the 3-day reversal window (display-only — the
 * Reversal_Service stays authoritative, R6.2).
 */

import type { Transaction } from '../api/types.js';

/**
 * Format a decimal-string amount with a currency code for display, keeping the
 * exact digits from the string and grouping the integer part with thousands
 * separators. Falls back to the raw input if it is not a plain decimal string,
 * so an unexpected shape never throws in the render path.
 *
 * @example formatMoney('1234.5', 'ZAR') // "R1,234.50"
 * @example formatMoney('-20', 'ZAR')    // "-R20.00"
 */
/**
 * Known currency symbols. South African Rand renders as "R" (e.g. R1,093.00)
 * as a local bank would show it; unknown codes fall back to the code + a
 * space so the amount is never rendered without a currency indicator.
 */
const CURRENCY_SYMBOLS: Readonly<Record<string, string>> = { ZAR: 'R' };

/** The display prefix for a currency: a symbol (e.g. "R") or "<CODE> ". */
function currencyPrefix(currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency.trim().toUpperCase()];
  return symbol !== undefined ? symbol : `${currency} `;
}

export function formatMoney(amount: string, currency: string): string {
  const match = /^(-)?(\d+)(?:\.(\d+))?$/.exec(amount.trim());
  if (match === null) {
    return `${currencyPrefix(currency)}${amount}`.trim();
  }
  const sign = match[1] ?? '';
  const integer = match[2] ?? '0';
  const fraction = (match[3] ?? '').padEnd(2, '0').slice(0, 2);
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${currencyPrefix(currency)}${grouped}.${fraction}`;
}

/** The category label, mapping null/empty to the "Uncategorized" bucket (R5.3). */
export function categoryLabel(category: string | null | undefined): string {
  const trimmed = (category ?? '').trim();
  return trimmed.length > 0 ? trimmed : 'Uncategorized';
}

/**
 * Render an ISO timestamp/date in the user's locale. Falls back to the raw
 * string if it cannot be parsed, so a bad value never breaks the row.
 */
export function formatDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  // Compact, single-line SA-locale format (e.g. "23 Sep 2026, 02:00") so
  // date cells do not wrap across several lines in the transactions table.
  return parsed.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Compact month names (British/SA style, e.g. "Sept") for natural dates. */
const SHORT_MONTHS: readonly string[] = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec',
];

/** Local-calendar midnight for a date, as epoch ms (for whole-day diffs). */
function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** "25 Sept" for the current year, "25 Sept 2025" otherwise — no time part. */
function compactDate(date: Date, now: Date): string {
  const month = SHORT_MONTHS[date.getMonth()] ?? '';
  const base = `${date.getDate()} ${month}`;
  return date.getFullYear() === now.getFullYear() ? base : `${base} ${date.getFullYear()}`;
}

/**
 * Natural, time-free date for feeds: "Today", "Yesterday", a compact date
 * ("25 Sept") within the current year, or "25 Sept 2025" for other years.
 * Falls back to the raw string if unparseable. `now` is injectable for tests.
 */
export function formatRelativeDate(iso: string, now: Date = new Date()): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  const dayDiff = Math.round((startOfLocalDay(now) - startOfLocalDay(parsed)) / 86_400_000);
  if (dayDiff === 0) {
    return 'Today';
  }
  if (dayDiff === 1) {
    return 'Yesterday';
  }
  return compactDate(parsed, now);
}

/**
 * Compact, time-free date without the relative words ("25 Sept" / "25 Sept
 * 2025"), for deadlines such as "Reversible until …". Falls back to the raw
 * string if unparseable.
 */
export function formatShortDate(iso: string, now: Date = new Date()): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return compactDate(parsed, now);
}

/**
 * Derive a readable description from a transaction's category and sign, since
 * the service returns no description field. Unknown categories are used as-is
 * (e.g. "Groceries"); null/"Uncategorized" becomes "Transaction".
 */
export function describeTransaction(tx: Pick<Transaction, 'amount' | 'category'>): string {
  const label = categoryLabel(tx.category);
  const debit = tx.amount.trim().startsWith('-');
  switch (label.toLowerCase()) {
    case 'transfer':
      return debit ? 'Transfer out' : 'Transfer in';
    case 'payment':
      return debit ? 'Payment to beneficiary' : 'Payment received';
    case 'salary':
      return 'Salary received';
    case 'refund':
      return 'Refund received';
    case 'uncategorized':
      return 'Transaction';
    default:
      return label;
  }
}

/**
 * The primary statement line for a transaction row, the way a South African
 * bank statement reads. When the transaction carries a {@link counterparty} it
 * is woven into a verb-phrase derived from the sign and category:
 *   - a debit with a `payment` category → "Payment to {counterparty}";
 *   - a debit with a `transfer` category → "Transfer to {counterparty}";
 *   - a debit with any other category → "Payment to {counterparty}" (a debit is
 *     money leaving, so it reads as a payment out);
 *   - a credit → "Transfer from {counterparty}" (money arriving from the other
 *     account / party).
 * When no counterparty is present (older services, seeded rows) it falls back
 * to the current derived {@link describeTransaction} text, so behaviour never
 * regresses. Whitespace-only counterparties count as absent, and the field is
 * tolerated as missing for responses from an older service.
 */
export function transactionPrimaryLine(
  tx: Pick<Transaction, 'amount' | 'category'> & {
    readonly counterparty?: string | null;
  },
): string {
  const counterparty = (tx.counterparty ?? '').trim();
  if (counterparty.length === 0) {
    return describeTransaction(tx);
  }
  const debit = tx.amount.trim().startsWith('-');
  if (!debit) {
    return `Transfer from ${counterparty}`;
  }
  const label = categoryLabel(tx.category).toLowerCase();
  const verb = label === 'transfer' ? 'Transfer to' : 'Payment to';
  return `${verb} ${counterparty}`;
}

/**
 * The primary line for a transaction row: the customer's own payment reference
 * when one was supplied (e.g. "Rent March"), otherwise the derived
 * {@link describeTransaction} text. Whitespace-only references count as absent,
 * and the field is tolerated as missing for responses from an older service.
 */
export function transactionTitle(
  tx: Pick<Transaction, 'amount' | 'category'> & { readonly reference?: string | null },
): string {
  const reference = (tx.reference ?? '').trim();
  return reference.length > 0 ? reference : describeTransaction(tx);
}

/**
 * The customer's own payment reference for a transaction, trimmed, or `null`
 * when none was supplied. Whitespace-only references count as absent, and the
 * field is tolerated as missing for responses from an older service. Feed rows
 * render this as a smaller, muted second line beneath {@link describeTransaction},
 * the way a bank statement shows the derived description with the customer's
 * reference underneath.
 */
export function transactionReference(
  tx: { readonly reference?: string | null },
): string | null {
  const reference = (tx.reference ?? '').trim();
  return reference.length > 0 ? reference : null;
}

/** Reversal window length mirrored client-side for display (R6.2). */
export const REVERSAL_WINDOW_DAYS = 3;

/**
 * The instant after which a transaction is no longer reversible: its
 * transaction date plus {@link REVERSAL_WINDOW_DAYS}. `undefined` when the
 * date cannot be parsed.
 */
export function reversalDeadline(transactionDate: string): Date | undefined {
  const parsed = new Date(transactionDate);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return new Date(parsed.getTime() + REVERSAL_WINDOW_DAYS * 86_400_000);
}

/**
 * Client-side eligibility mirror: a transaction is offered a Reverse action
 * only when it is `posted` and `now` is within the 3-day window. Display-only
 * guard — the server still decides (`REVERSAL_OUT_OF_WINDOW`, `ALREADY_REVERSED`).
 */
export function isReversibleNow(
  tx: Pick<Transaction, 'status' | 'transactionDate'>,
  now: Date = new Date(),
): boolean {
  if (tx.status.toLowerCase() !== 'posted') {
    return false;
  }
  const deadline = reversalDeadline(tx.transactionDate);
  return deadline !== undefined && now.getTime() <= deadline.getTime();
}

/** Today's date as `YYYY-MM-DD`, for defaulting insight date inputs. */
export function todayIso(): string {
  const now = new Date();
  const isoPart = now.toISOString().slice(0, 10);
  return isoPart;
}

/** The date `days` before today as `YYYY-MM-DD`. */
export function isoDaysAgo(days: number): string {
  const then = new Date();
  then.setDate(then.getDate() - days);
  return then.toISOString().slice(0, 10);
}

/**
 * Validate a client-side transfer amount so the form can guard before calling
 * the service (mirrors R3.7/R4.6: 0.01–999,999,999.99, at most two decimals).
 * Returns a message describing the problem, or `undefined` when valid. The
 * server remains the source of truth and its envelope is still rendered.
 */
export function validateAmount(amount: string): string | undefined {
  const trimmed = amount.trim();
  if (trimmed.length === 0) {
    return 'Enter an amount.';
  }
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return 'Amount must be a number with at most two decimal places.';
  }
  const value = Number(trimmed);
  if (value < 0.01 || value > 999999999.99) {
    return 'Amount must be between 0.01 and 999,999,999.99.';
  }
  return undefined;
}

/** Maximum length of a payment reference after trimming (mirrors Transfer_Service). */
export const REFERENCE_MAX_LENGTH = 30;

/**
 * Characters a payment reference may contain — letters, digits, spaces and
 * `. , - / & '` — mirroring the Transfer_Service rule so the form can flag a
 * bad reference before the request is sent. Anchored over the whole string.
 */
const REFERENCE_PATTERN = /^[A-Za-z0-9 .,\-/&']+$/;

/** Helper text shown under the reference input on both transfer forms. */
export const REFERENCE_HINT = `Optional, up to ${REFERENCE_MAX_LENGTH} characters, e.g. Rent March.`;

/** Friendly message for a reference the service (or the client) rejected. */
export const REFERENCE_INVALID_MESSAGE =
  `Reference may only use letters, digits, spaces and . , - / & ' (up to ${REFERENCE_MAX_LENGTH} characters).`;

/**
 * Trim a raw reference for sending: returns the trimmed text, or `undefined`
 * when the customer left it blank so the request body omits the key entirely
 * (the service then stores `null`, not an empty string).
 */
export function normalizeReference(raw: string): string | undefined {
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Validate an optional client-side payment reference (mirrors the service's
 * `INVALID_REFERENCE` rule: after trimming, 1–{@link REFERENCE_MAX_LENGTH}
 * characters from {@link REFERENCE_PATTERN}). Blank is valid because the field
 * is optional. Returns a message describing the problem, or `undefined` when
 * valid. The server remains authoritative and its envelope is still rendered.
 */
export function validateReference(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return undefined;
  }
  if (trimmed.length > REFERENCE_MAX_LENGTH) {
    return `Reference must be at most ${REFERENCE_MAX_LENGTH} characters.`;
  }
  if (!REFERENCE_PATTERN.test(trimmed)) {
    return "Reference may only contain letters, digits, spaces and . , - / & '";
  }
  return undefined;
}
